import type { App } from '../../app.js';
import { h, button } from '../dom.js';
import { allTrackDefs, VEHICLES, ticksToTimeString } from '@voidline/shared';
import { levelForXp, xpForLevel } from '../../persist.js';

const MEDAL_ICONS: Record<string, string> = { bronze: '🥉', silver: '🥈', gold: '🥇', author: '◆' };

export function buildStatsScreen(app: App): HTMLElement {
  const p = app.progression;
  const level = levelForXp(p.xp);
  const tracks = allTrackDefs();

  const stat = (label: string, value: string) =>
    h('div', { class: 'col', style: 'gap: 2px; min-width: 110px;' },
      h('div', { class: 'mono', style: 'font-size: 26px; color: var(--accent);', text: value }),
      h('div', { class: 'subtle', style: 'font-size: 10px;', text: label }));

  const trackRows = tracks
    .map((t) => {
      const rec = p.records[t.id];
      if (!rec) return null;
      return h(
        'div',
        { class: 'row', style: 'justify-content: space-between; padding: 6px 12px; border-bottom: 1px solid var(--line); gap: 16px;' },
        h('span', { style: 'font-size: 13px;', text: `${t.name} ${rec.medal ? MEDAL_ICONS[rec.medal] : ''}` }),
        h('span', {
          class: 'mono subtle',
          style: 'font-size: 12px;',
          text: `lap ${rec.bestLapTicks ? ticksToTimeString(rec.bestLapTicks) : '—'} · race ${
            rec.bestRaceTicks ? ticksToTimeString(rec.bestRaceTicks) : '—'
          } · ${rec.wins}W/${rec.races}R`,
        })
      );
    })
    .filter(Boolean) as HTMLElement[];

  const vehicleRows = VEHICLES.map((v) => {
    const vs = p.vehicleStats[v.id];
    if (!vs) return null;
    return h(
      'div',
      { class: 'row', style: 'justify-content: space-between; padding: 6px 12px; border-bottom: 1px solid var(--line);' },
      h('span', { style: 'font-size: 13px;', text: v.name }),
      h('span', { class: 'mono subtle', style: 'font-size: 12px;', text: `${vs.wins}W / ${vs.races}R` })
    );
  }).filter(Boolean) as HTMLElement[];

  return h(
    'div',
    { class: 'screen screen-dim' },
    h(
      'div',
      { class: 'panel col', style: 'width: min(680px, 94vw); max-height: 88vh; overflow-y: auto; gap: 18px;' },
      h(
        'div',
        { class: 'row', style: 'justify-content: space-between;' },
        h('div', { class: 'h1', text: 'SERVICE RECORD' }),
        button('◄ BACK', () => {
          app.audio.play('ui_back');
          app.show('menu');
        }, 'small focusable')
      ),
      h('div', { class: 'subtle', style: 'letter-spacing: 0.2em;', text: `${(app.settings.playerName || 'PILOT').toUpperCase()} · LEVEL ${level}` }),
      h('div', { class: 'statbar', style: 'height: 8px;' },
        h('div', { style: `width: ${Math.round(((p.xp - xpForLevel(level)) / Math.max(1, xpForLevel(level + 1) - xpForLevel(level))) * 100)}%` })),
      h('div', { class: 'row wrap', style: 'gap: 26px;' },
        stat('RACES', String(p.races)),
        stat('WINS', String(p.wins)),
        stat('PODIUMS', String(p.podiums)),
        stat('XP', String(p.xp)),
        stat('MEDALS', String(Object.values(p.records).filter((r) => r.medal).length))
      ),
      trackRows.length
        ? h('div', {},
            h('div', { class: 'h2', text: 'TRACK RECORDS', style: 'margin-bottom: 8px;' }),
            ...trackRows)
        : h('div', { class: 'subtle', style: 'text-transform: none;', text: 'Race something — records will appear here.' }),
      vehicleRows.length
        ? h('div', {},
            h('div', { class: 'h2', text: 'CRAFT SERVICE', style: 'margin-bottom: 8px;' }),
            ...vehicleRows)
        : null
    )
  );
}
